import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    Loader2,
    Play,
    RotateCcw,
    XCircle,
} from 'lucide-react';
import { useApiKey } from '@/stores/apiKey.store';
import { useScenario, type ScenarioStep } from '@/hooks/useScenarios';
import { extract, substituteVars, substituteDeep } from '@/lib/jsonpath';
import MethodBadge from '@/components/layout/MethodBadge';
import CodeBlock from '@/components/code/CodeBlock';
import ConnectCta from '@/components/ConnectCta';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) || 'https://api.satutim.id';

type StepStatus = 'pending' | 'running' | 'success' | 'failed';

interface StepResult {
    status: StepStatus;
    httpStatus?: number;
    elapsedMs?: number;
    resolvedPath?: string;
    resolvedBody?: unknown;
    response?: unknown;
    error?: string;
    extracted?: Record<string, unknown>;
}

export default function ScenarioPage() {
    const { id } = useParams<{ id: string }>();
    const { key } = useApiKey();
    const { scenario, loading, error } = useScenario(id);

    const initialResults = useMemo<Record<string, StepResult>>(() => {
        if (!scenario) return {};
        const r: Record<string, StepResult> = {};
        for (const s of scenario.steps) r[s.id] = { status: 'pending' };
        return r;
    }, [scenario]);

    const [results, setResults] = useState<Record<string, StepResult>>(initialResults);
    const [running, setRunning] = useState(false);

    // Re-init results when scenario id changes (or scenario loads).
    useMemo(() => setResults(initialResults), [initialResults]);

    const handleReset = useCallback(() => {
        setResults(initialResults);
        setRunning(false);
    }, [initialResults]);

    const handleRun = useCallback(async () => {
        if (!scenario || !key || running) return;
        setRunning(true);

        const vars: Record<string, unknown> = {};
        const nextResults: Record<string, StepResult> = { ...initialResults };
        setResults(nextResults);

        for (const step of scenario.steps) {
            const resolvedPath = substituteVars(step.path, vars);
            const resolvedBody = step.body ? substituteDeep(step.body, vars) : undefined;

            // Mark running
            nextResults[step.id] = {
                status: 'running',
                resolvedPath,
                resolvedBody,
            };
            setResults({ ...nextResults });

            const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
            try {
                const url = `${API_BASE}${resolvedPath}`;
                const headers: Record<string, string> = {
                    Authorization: `Bearer ${key}`,
                };
                const init: RequestInit = { method: step.method, headers };
                if (resolvedBody !== undefined && step.method !== 'GET' && step.method !== 'DELETE') {
                    headers['Content-Type'] = 'application/json';
                    init.body = JSON.stringify(resolvedBody);
                }
                const res = await fetch(url, init);
                const elapsedMs = Math.round(
                    (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0,
                );
                const rawText = await res.text();
                let body: unknown = rawText;
                const ct = res.headers.get('content-type') || '';
                if (ct.includes('json') && rawText) {
                    try { body = JSON.parse(rawText); } catch { /* keep raw */ }
                }

                if (!res.ok) {
                    nextResults[step.id] = {
                        status: 'failed',
                        httpStatus: res.status,
                        elapsedMs,
                        resolvedPath,
                        resolvedBody,
                        response: body,
                        error: `HTTP ${res.status} ${res.statusText}`,
                    };
                    setResults({ ...nextResults });
                    break; // stop on first failure
                }

                // Extract vars
                const extracted: Record<string, unknown> = {};
                if (step.extract) {
                    for (const [name, path] of Object.entries(step.extract)) {
                        const val = extract(body, path);
                        extracted[name] = val;
                        if (val !== undefined) vars[name] = val;
                    }
                }

                nextResults[step.id] = {
                    status: 'success',
                    httpStatus: res.status,
                    elapsedMs,
                    resolvedPath,
                    resolvedBody,
                    response: body,
                    extracted,
                };
                setResults({ ...nextResults });
            } catch (err) {
                const elapsedMs = Math.round(
                    (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0,
                );
                nextResults[step.id] = {
                    status: 'failed',
                    elapsedMs,
                    resolvedPath,
                    resolvedBody,
                    error: err instanceof Error ? err.message : 'Request gagal',
                };
                setResults({ ...nextResults });
                break;
            }
        }

        setRunning(false);
    }, [scenario, key, running, initialResults]);

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto px-6 lg:px-8 py-10 text-sm" style={{ color: 'var(--muted)' }}>
                Memuat scenario…
            </div>
        );
    }

    if (error || !scenario) {
        return (
            <div className="max-w-4xl mx-auto px-6 lg:px-8 py-10">
                <Link to="/scenarios" className="inline-flex items-center gap-1 text-xs mb-4 transition-colors hover:text-ink"
                    style={{ color: 'var(--muted)' }}>
                    <ArrowLeft className="h-3 w-3" /> Kembali ke daftar
                </Link>
                <div
                    className="px-4 py-3 rounded-lg text-sm"
                    style={{
                        backgroundColor: 'var(--err-bg)',
                        color: 'var(--err)',
                        border: '1px solid var(--err-border)',
                    }}
                >
                    {error || 'Scenario tidak ditemukan'}
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-10">
            <Link to="/scenarios" className="inline-flex items-center gap-1 text-xs mb-4 transition-colors hover:opacity-80"
                style={{ color: 'var(--muted)' }}>
                <ArrowLeft className="h-3 w-3" /> Semua scenario
            </Link>

            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                <div className="flex-1 min-w-0">
                    <h1 className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--ink)' }}>
                        {scenario.title}
                    </h1>
                    <p className="text-sm leading-relaxed mt-2 max-w-2xl" style={{ color: 'var(--muted)' }}>
                        {scenario.description}
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={handleReset}
                        disabled={running}
                        className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md transition-colors disabled:opacity-50"
                        style={{
                            backgroundColor: 'var(--bg)',
                            color: 'var(--ink)',
                            border: '1px solid var(--border)',
                        }}
                    >
                        <RotateCcw className="h-3.5 w-3.5" /> Reset
                    </button>
                    <button
                        type="button"
                        onClick={handleRun}
                        disabled={!key || running}
                        className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: 'var(--accent)', color: 'var(--on-accent)' }}
                    >
                        {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                        {running ? 'Berjalan…' : 'Jalankan scenario'}
                    </button>
                </div>
            </div>

            {!key && (
                <div className="mt-6">
                    <ConnectCta
                        variant="banner"
                        title="Butuh API key buat menjalankan scenario"
                        description="Tombol Jalankan akan aktif setelah kamu login dan punya akses API."
                    />
                </div>
            )}

            <ol className="mt-6 space-y-3">
                {scenario.steps.map((step, idx) => (
                    <StepCard
                        key={step.id}
                        index={idx + 1}
                        step={step}
                        result={results[step.id] || { status: 'pending' }}
                    />
                ))}
            </ol>
        </div>
    );
}

interface StepCardProps {
    index: number;
    step: ScenarioStep;
    result: StepResult;
}

function StepCard({ index, step, result }: StepCardProps) {
    const [bodyOpen, setBodyOpen] = useState(false);
    const [responseOpen, setResponseOpen] = useState(true);

    const statusColor =
        result.status === 'success' ? 'var(--accent)'
        : result.status === 'failed' ? 'var(--err)'
        : result.status === 'running' ? 'var(--warn)'
        : 'var(--ghost)';
    const statusLabel =
        result.status === 'success' ? 'success'
        : result.status === 'failed' ? 'failed'
        : result.status === 'running' ? 'running'
        : 'pending';

    const extractedEntries = result.extracted ? Object.entries(result.extracted) : [];
    const hasBody = !!step.body;

    return (
        <li
            className="rounded-lg p-4"
            style={{
                backgroundColor: 'var(--bg)',
                border: `1px solid ${result.status === 'failed' ? 'var(--err-border)' : 'var(--border)'}`,
            }}
        >
            <div className="flex items-start gap-3">
                <div
                    className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
                    style={{
                        backgroundColor: 'var(--surface-1)',
                        color: 'var(--ink)',
                        border: '1px solid var(--border)',
                    }}
                >
                    {index}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <MethodBadge method={step.method} size="xs" />
                        <h3 className="text-sm font-semibold truncate" style={{ color: 'var(--ink)' }}>{step.label}</h3>
                        <span
                            className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{
                                color: statusColor,
                                backgroundColor: `${statusColor}15`,
                                border: `1px solid ${statusColor}30`,
                            }}
                        >
                            {result.status === 'running' && <Loader2 className="h-3 w-3 animate-spin" />}
                            {result.status === 'success' && <CheckCircle2 className="h-3 w-3" />}
                            {result.status === 'failed' && <XCircle className="h-3 w-3" />}
                            {statusLabel}
                        </span>
                        {typeof result.elapsedMs === 'number' && (
                            <span className="text-xs" style={{ color: 'var(--ghost)' }}>{result.elapsedMs}ms</span>
                        )}
                        {typeof result.httpStatus === 'number' && (
                            <span className="text-xs" style={{ color: 'var(--ghost)' }}>HTTP {result.httpStatus}</span>
                        )}
                    </div>
                    <PathDisplay raw={step.path} resolved={result.resolvedPath} />

                    {hasBody && (
                        <div className="mt-2">
                            <button
                                type="button"
                                onClick={() => setBodyOpen((v) => !v)}
                                className="inline-flex items-center gap-1 text-xxs text-muted hover:text-ink"
                            >
                                {bodyOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                Request body
                            </button>
                            {bodyOpen && (
                                <div className="mt-1.5">
                                    <CodeBlock
                                        code={JSON.stringify(result.resolvedBody ?? step.body ?? {}, null, 2)}
                                        lang="json"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {result.error && (
                        <div
                            className="mt-2 px-3 py-2 rounded-md text-xs"
                            style={{
                                backgroundColor: 'var(--err-bg)',
                                color: 'var(--err)',
                                border: '1px solid var(--err-border)',
                            }}
                        >
                            {result.error}
                        </div>
                    )}

                    {result.response !== undefined && (
                        <div className="mt-2">
                            <button
                                type="button"
                                onClick={() => setResponseOpen((v) => !v)}
                                className="inline-flex items-center gap-1 text-xxs text-muted hover:text-ink"
                            >
                                {responseOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                Response
                            </button>
                            {responseOpen && (
                                <div className="mt-1.5">
                                    <CodeBlock
                                        code={formatResponse(result.response)}
                                        lang="json"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {extractedEntries.length > 0 && (
                        <div
                            className="mt-3 px-3 py-2 rounded-md"
                            style={{
                                backgroundColor: 'var(--surface-1)',
                                border: '1px solid var(--border)',
                            }}
                        >
                            <p className="text-[11px] uppercase tracking-wider mb-1.5 inline-flex items-center gap-1 font-medium" style={{ color: 'var(--ghost)' }}>
                                <ArrowRight className="h-3 w-3" /> Extracted vars
                            </p>
                            <div className="space-y-1">
                                {extractedEntries.map(([name, val]) => (
                                    <div key={name} className="font-mono text-xs flex items-center gap-2">
                                        <span style={{ color: 'var(--accent)' }}>${'{'}{name}{'}'}</span>
                                        <span style={{ color: 'var(--ghost)' }}>=</span>
                                        <span className="truncate" style={{ color: 'var(--muted)' }}>{formatValue(val)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </li>
    );
}

function PathDisplay({ raw, resolved }: { raw: string; resolved?: string }) {
    // Highlight ${vars} in raw path.
    const parts = raw.split(/(\$\{[^}]+\})/g);
    return (
        <div className="mt-1.5 font-mono text-xs leading-snug break-all">
            <div className="text-muted">
                {parts.map((p, i) =>
                    /^\$\{[^}]+\}$/.test(p) ? (
                        <span key={i} style={{ color: 'var(--warn)' }}>{p}</span>
                    ) : (
                        <span key={i}>{p}</span>
                    ),
                )}
            </div>
            {resolved && resolved !== raw && (
                <div className="text-ghost text-xxs mt-0.5">→ {resolved}</div>
            )}
        </div>
    );
}

function formatResponse(value: unknown): string {
    try { return JSON.stringify(value, null, 2); }
    catch { return String(value); }
}

function formatValue(v: unknown): string {
    if (v === undefined) return 'undefined';
    if (v === null) return 'null';
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v); }
    catch { return String(v); }
}
